/**
 * Komal OS - Vendor Directory: vendor detail.
 *
 * Vendor info (+ a one-click link to verify the GSTIN on the official
 * government portal - see GST_PORTAL_SEARCH_URL in vendor_routes.py for
 * why this is a link, not an automated check), supply items, contacts
 * (manual or scanned), and a plain notes thread. Editing is fully shared
 * across everyone with Vendor Directory access - no ownership lock.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Plus, Phone, Mail, ArrowLeft, Pencil, Trash2, ExternalLink, Camera, Loader2,
  CheckCircle2, AlertTriangle, X, Clock, PenLine,
} from 'lucide-react';
import {
  getVendor, updateVendor, deleteVendor, addVendorContact, updateVendorContact, deleteVendorContact, addVendorNote,
  listSupplyItems, createSupplyItem, scanVendorCard, getVendorScanJob, confirmVendorCard,
} from '../../services/vendorService';
import useUIStore from '../../store/uiStore';
import useAuthStore from '../../store/authStore';

const CATEGORIES = ['Material Supplier', 'Equipment', 'Contractor', 'Services', 'Other'];
const emptyContactForm = () => ({ name: '', title: '', phone: '', email: '' });
const SCAN_FIELD_LABELS = { name: 'Name', title: 'Title', company: 'Company', phone: 'Phone', email: 'Email' };
const SCAN_FIELD_ORDER = ['name', 'title', 'company', 'phone', 'email'];

export default function VendorDetailPage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useUIStore();
  const { user } = useAuthStore();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supplyItems, setSupplyItems] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [newSupplyName, setNewSupplyName] = useState('');

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm());
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactForm, setEditContactForm] = useState(emptyContactForm());
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  // Card scan state (single-card, inline)
  const [scanning, setScanning] = useState(false);
  const [scanJob, setScanJob] = useState(null);
  const [scanFields, setScanFields] = useState(null);
  const [scanConfidence, setScanConfidence] = useState({});
  const [scanHandwrittenNote, setScanHandwrittenNote] = useState('');
  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  const load = () => getVendor(vendorId).then((res) => setVendor(res.data.vendor)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    listSupplyItems().then((res) => setSupplyItems(res.data.supply_items)).catch(() => {});
    return () => clearTimeout(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const startEdit = () => {
    setEditForm({
      name: vendor.name, category: vendor.category || '', gstin: vendor.gstin || '',
      phone: vendor.phone || '', email: vendor.email || '', address: vendor.address || '', notes: vendor.notes_text || '',
      supply_item_ids: vendor.supply_items.map((s) => s.id),
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateVendor(vendorId, editForm);
      showToast('Vendor updated', 'success');
      setEditing(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update vendor', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleEditSupplyItem = (id) => {
    setEditForm((f) => ({
      ...f,
      supply_item_ids: f.supply_item_ids.includes(id) ? f.supply_item_ids.filter((x) => x !== id) : [...f.supply_item_ids, id],
    }));
  };

  const addNewSupplyItem = async () => {
    if (!newSupplyName.trim()) return;
    try {
      const res = await createSupplyItem(newSupplyName.trim());
      setSupplyItems((prev) => (prev.some((s) => s.id === res.data.supply_item.id) ? prev : [...prev, res.data.supply_item]));
      setEditForm((f) => ({ ...f, supply_item_ids: [...f.supply_item_ids, res.data.supply_item.id] }));
      setNewSupplyName('');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add product', 'error');
    }
  };

  const handleDeleteVendor = async () => {
    // CEO-only server-side too (see vendor_routes.py's require_ceo) - this
    // is a real, permanent delete (contacts and notes included), not an
    // archive, so it asks before doing anything irreversible.
    if (!window.confirm(`Permanently delete "${vendor.name}" and all its contacts and notes? This cannot be undone.`)) return;
    try {
      const res = await deleteVendor(vendorId);
      showToast(res.data.message || 'Vendor deleted', 'success');
      navigate('/vendors');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not delete vendor', 'error');
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      await addVendorContact(vendorId, contactForm);
      showToast('Contact added', 'success');
      setContactForm(emptyContactForm());
      setShowContactForm(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await deleteVendorContact(vendorId, contactId);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not remove contact', 'error');
    }
  };

  const startEditContact = (c) => {
    setEditingContactId(c.id);
    setEditContactForm({ name: c.name, title: c.title || '', phone: c.phone || '', email: c.email || '' });
  };

  const submitEditContact = async (e) => {
    e.preventDefault();
    if (!editContactForm.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      await updateVendorContact(vendorId, editingContactId, editContactForm);
      showToast('Contact updated', 'success');
      setEditingContactId(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await addVendorNote(vendorId, noteText.trim());
      setNoteText('');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add note', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- card scan (single, inline) ---
  const resetScan = () => {
    setScanJob(null);
    setScanFields(null);
    setScanConfidence({});
    setScanHandwrittenNote('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const applyScanResult = (job) => {
    const r = job.result || {};
    setScanFields({ name: r.name || '', title: r.title || '', company: r.company || vendor.name, phone: r.phone || '', email: r.email || '' });
    setScanConfidence(r.confidence || {});
    setScanHandwrittenNote(r.handwritten_note || '');
  };

  const pollScanJob = (jobId) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await getVendorScanJob(jobId);
        setScanJob(res.data.job);
        if (res.data.job.status === 'done') applyScanResult(res.data.job);
        else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
        else pollScanJob(jobId);
      } catch {
        pollScanJob(jobId);
      }
    }, 5000);
  };

  const handleFileChosen = async (file) => {
    if (!file) return;
    setScanning(true);
    resetScan();
    try {
      const res = await scanVendorCard(file);
      setScanJob(res.data.job);
      if (res.data.job.status === 'done') applyScanResult(res.data.job);
      else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
      else pollScanJob(res.data.job.id);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not scan the card', 'error');
    } finally {
      setScanning(false);
    }
  };

  const confirmScannedContact = async () => {
    setSaving(true);
    try {
      const res = await confirmVendorCard({
        job_id: scanJob.id, vendor_id: Number(vendorId), ...scanFields,
        handwritten_note: scanHandwrittenNote.trim() || null,
      });
      showToast(`Saved ${res.data.contact.name}`, 'success');
      resetScan();
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not save this contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen p-6 text-white/80 text-sm">Loading…</div>;
  if (!vendor) return <div className="min-h-screen p-6 text-white/80 text-sm">Vendor not found.</div>;

  const scanBusy = scanJob && ['pending', 'processing', 'queued'].includes(scanJob.status);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/vendors" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-3">
          <ArrowLeft size={13} /> Vendors
        </Link>

        <div className="flex items-start justify-between mb-6 gap-3">
          {!editing ? (
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                {vendor.name}
                <button onClick={startEdit} className="text-white/60 hover:text-white"><Pencil size={14} /></button>
              </h1>
              <p className="text-sm text-white/70 mt-0.5">
                {[vendor.category, vendor.address].filter(Boolean).join(' · ') || 'No details yet'} · Added by {vendor.created_by?.name || '—'}
              </p>
              <div className="flex items-center gap-3 mt-1.5 text-sm text-white/70">
                {vendor.phone && <span className="flex items-center gap-1"><Phone size={12} />{vendor.phone}</span>}
                {vendor.email && <span className="flex items-center gap-1"><Mail size={12} />{vendor.email}</span>}
              </div>
              {vendor.gstin && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs font-mono text-white/80 bg-white/10 border border-white/20 rounded px-2 py-1">{vendor.gstin}</span>
                  <a
                    href={vendor.gst_verify_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white underline"
                  >
                    Verify on GST portal <ExternalLink size={11} />
                  </a>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={saveEdit} className="glass-card p-5 w-full">
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Vendor name" />
                <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                  <option value="">Category…</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={editForm.gstin} onChange={(e) => setEditForm({ ...editForm, gstin: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="GSTIN" />
                <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Phone" />
                <input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Email" />
                <input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Address" />
              </div>
              <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3" placeholder="General notes" rows={2} />

              <div className="text-[10px] font-semibold uppercase text-gray-500 mb-1.5">Products supplied</div>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {supplyItems.map((s) => (
                  <button type="button" key={s.id} onClick={() => toggleEditSupplyItem(s.id)}
                    className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${editForm.supply_item_ids.includes(s.id) ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white text-gray-600 border-gray-300'}`}>
                    {s.name}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 mb-4">
                <input placeholder="Add a new product…" value={newSupplyName} onChange={(e) => setNewSupplyName(e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs" />
                <button type="button" onClick={addNewSupplyItem} className="text-xs font-semibold text-komal-blue border border-gray-300 rounded px-2">Add</button>
              </div>

              <div className="flex gap-2">
                <button disabled={saving} className="text-sm font-semibold text-white bg-green-700 rounded px-4 py-2">Save</button>
                <button type="button" onClick={() => setEditing(false)} className="text-sm text-gray-500 px-2">Cancel</button>
                {user?.role === 'CEO' && (
                  <button type="button" onClick={handleDeleteVendor} className="ml-auto flex items-center gap-1 text-xs font-medium text-red-600 hover:underline">
                    <Trash2 size={13} /> Permanently delete vendor
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {!editing && (
          <div className="grid gap-5 md:grid-cols-[300px_1fr]">
            <div className="flex flex-col gap-4">
              {/* Products supplied */}
              <div className="glass-card p-4">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">Products supplied</div>
                <div className="flex flex-wrap gap-1.5">
                  {vendor.supply_items.length === 0 ? <span className="text-xs text-gray-400">None tagged yet — edit to add.</span> : vendor.supply_items.map((s) => (
                    <span key={s.id} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{s.name}</span>
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Contacts ({vendor.contacts.length})</span>
                  <div className="flex items-center gap-2">
                    <label htmlFor="vendor-card-scan" className="text-komal-blue cursor-pointer" title="Scan a card">
                      {scanning ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
                    </label>
                    <input id="vendor-card-scan" ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => handleFileChosen(e.target.files?.[0])} />
                    <button onClick={() => setShowContactForm((v) => !v)} className="text-komal-blue"><Plus size={15} /></button>
                  </div>
                </div>

                {scanBusy && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5 mb-3 text-xs text-gray-600">
                    <Loader2 size={13} className="animate-spin text-komal-blue shrink-0" />
                    {scanJob.status === 'queued' ? `Provider busy — retrying${scanJob.eta_seconds ? ` in ~${scanJob.eta_seconds}s` : ''}` : 'Scanning card…'}
                  </div>
                )}

                {scanFields && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-gray-600 flex items-center gap-1"><CheckCircle2 size={12} className="text-green-600" /> Review scanned card</span>
                      <button onClick={resetScan} className="text-gray-400 hover:text-gray-600"><X size={13} /></button>
                    </div>
                    <div className="grid gap-1.5">
                      {SCAN_FIELD_ORDER.map((f) => {
                        const isLow = scanConfidence[f] === 'low';
                        return (
                          <div key={f}>
                            <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase">
                              {SCAN_FIELD_LABELS[f]} {isLow && <AlertTriangle size={9} className="text-amber-600" />}
                            </label>
                            <input value={scanFields[f]} onChange={(e) => setScanFields({ ...scanFields, [f]: e.target.value })}
                              className={`w-full border rounded px-2 py-1 text-xs ${isLow ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`} />
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-1.5">
                      <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-500 uppercase">
                        <PenLine size={9} /> Handwritten note (if any)
                      </label>
                      <textarea
                        value={scanHandwrittenNote}
                        onChange={(e) => setScanHandwrittenNote(e.target.value)}
                        placeholder="e.g. Asked for a quote by Friday"
                        rows={2}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <button onClick={confirmScannedContact} disabled={saving} className="w-full mt-2.5 text-xs font-semibold text-white bg-green-700 rounded px-2 py-1.5 disabled:opacity-60">
                      Confirm &amp; save contact
                    </button>
                  </div>
                )}

                {showContactForm && (
                  <form onSubmit={submitContact} className="grid gap-1.5 mb-3 bg-gray-50 rounded-lg p-2.5">
                    <input required placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                    <input placeholder="Title" value={contactForm.title} onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                    <input placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                    <input placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs" />
                    <button disabled={saving} className="text-xs font-semibold text-white bg-komal-blue rounded px-2 py-1.5">Add contact</button>
                  </form>
                )}

                {vendor.contacts.length === 0 ? (
                  <p className="text-xs text-gray-400">No contacts yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {vendor.contacts.map((c) => (
                      <li key={c.id} className="py-2 text-xs">
                        {editingContactId === c.id ? (
                          <form onSubmit={submitEditContact} className="grid gap-1.5 bg-gray-50 rounded-lg p-2">
                            <input required placeholder="Name" value={editContactForm.name} onChange={(e) => setEditContactForm({ ...editContactForm, name: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                            <input placeholder="Title" value={editContactForm.title} onChange={(e) => setEditContactForm({ ...editContactForm, title: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                            <input placeholder="Phone" value={editContactForm.phone} onChange={(e) => setEditContactForm({ ...editContactForm, phone: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                            <input placeholder="Email" value={editContactForm.email} onChange={(e) => setEditContactForm({ ...editContactForm, email: e.target.value })} className="border border-gray-300 rounded px-2 py-1 text-xs" />
                            <div className="flex gap-2">
                              <button disabled={saving} className="text-xs font-semibold text-white bg-green-700 rounded px-2 py-1">Save</button>
                              <button type="button" onClick={() => setEditingContactId(null)} className="text-xs text-gray-500 px-1">Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-semibold text-gray-800">{c.name}</div>
                              {c.title && <div className="text-gray-500">{c.title}</div>}
                              <div className="flex gap-3 mt-1 text-gray-400">
                                {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
                                {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                              </div>
                              {c.source === 'card_scan' && <span className="inline-block mt-1 text-[10px] font-semibold text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-1.5 py-0.5">Card scan</span>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button onClick={() => startEditContact(c)} className="text-gray-400 hover:text-komal-blue" title="Edit"><Pencil size={12} /></button>
                              <button onClick={() => handleDeleteContact(c.id)} className="text-gray-300 hover:text-red-500" title="Delete"><Trash2 size={12} /></button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="glass-card p-4">
              <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">Notes</div>
              <form onSubmit={submitNote} className="flex gap-2 mb-3">
                <input placeholder="Add a note — call, quote received, site visit…" value={noteText} onChange={(e) => setNoteText(e.target.value)} className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm" />
                <button disabled={saving} className="text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2">Add</button>
              </form>
              {vendor.activity.length === 0 ? (
                <p className="text-sm text-gray-400">No notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {vendor.activity.map((n) => (
                    <li key={n.id} className="text-sm border-l-2 border-gray-200 pl-3">
                      <div className="text-gray-800">{n.note_text}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <Clock size={10} /> {n.author?.name || 'System'} · {new Date(n.created_at).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
